// src/pages/PlatformBillingSuccess.js
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}
function normalizePlatformRole(v) {
  const r = safeString(v).trim().toUpperCase();
  if (r === "SUPER_ADMIN" || r === "ADMIN" || r === "SUPPORT") return r;
  return "";
}

export default function PlatformBillingSuccess() {
  const nav = useNavigate();
  const auth = useAuth();
  const [params] = useSearchParams();

  const token = safeString(auth?.token).trim();
  const isAuthed = !!token;

  // Route-authoritative platform detection (works even if mode flag is stale)
  const pathIsPlatform =
    typeof window !== "undefined" &&
    safeString(window.location.pathname).startsWith("/platform");

  const mode = safeString(auth?.mode).toLowerCase().trim();
  const inPlatformMode = pathIsPlatform || mode === "platform" || !!auth?.isPlatform;

  const platformRole = normalizePlatformRole(
    auth?.user?.platformRole ?? auth?.platformRole ?? ""
  );
  const hasPlatformAccess = !!platformRole;

  const apiGet = auth?.apiGet;

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [summary, setSummary] = useState(null);

  // Optional: read Stripe Checkout session id
  const sessionId = safeString(params.get("session_id")).trim();

  // Static lines (no hook needed)
  const lines = [
    "Your payment was completed and your subscription is now active.",
    "You can manage plans, invoices, and cancellations any time from Billing.",
  ];

  // Optional: if your backend has a billing summary endpoint, show “synced”
  useEffect(() => {
    let mounted = true;

    async function loadSummary() {
      try {
        setErr("");

        // Only attempt if it makes sense
        if (!isAuthed || !hasPlatformAccess || !inPlatformMode) return;
        if (typeof apiGet !== "function") return;

        setLoading(true);

        // Optional endpoint:
        // GET /api/platform/billing/summary -> { stats, rows, ... }
        const res = await apiGet("/api/platform/billing/summary");
        if (!mounted) return;

        if (res && typeof res === "object") setSummary(res);
      } catch {
        // Ignore if not implemented / not available yet
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadSummary();
    return () => {
      mounted = false;
    };
  }, [apiGet, hasPlatformAccess, inPlatformMode, isAuthed]);

  // Guards (match your platform pages)
  if (!isAuthed) {
    return (
      <div className="ft-page">
        <div className="ft-header">
          <div className="ft-header__text">
            <h1 className="ft-title">Payment successful</h1>
            <p className="ft-subtitle">Please sign in to continue.</p>
          </div>
          <div className="ft-header__actions">
            <button
              className="ft-btn ft-btn--primary"
              type="button"
              onClick={() => nav("/login")}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasPlatformAccess) {
    return (
      <div className="ft-page">
        <div className="ft-header">
          <div className="ft-header__text">
            <h1 className="ft-title">Payment successful</h1>
            <p className="ft-subtitle">This account does not have Platform access.</p>
          </div>
          <div className="ft-header__actions">
            <button
              className="ft-btn ft-btn--ghost"
              type="button"
              onClick={() => nav("/dashboard")}
            >
              Go to Store
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!inPlatformMode) {
    return (
      <div className="ft-page">
        <div className="ft-header">
          <div className="ft-header__text">
            <h1 className="ft-title">Payment successful</h1>
            <p className="ft-subtitle">Switch to Platform mode using the sidebar toggle.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ft-page">
      <div className="ft-header">
        <div className="ft-header__text">
          <h1 className="ft-title">Payment successful</h1>
          <p className="ft-subtitle">Subscription activated.</p>
        </div>

        <div className="ft-header__actions">
          <button
            className="ft-btn ft-btn--primary"
            type="button"
            onClick={() => nav("/platform/billing")}
          >
            Go to Billing
          </button>
          <button
            className="ft-btn ft-btn--ghost"
            type="button"
            onClick={() => nav("/platform")}
          >
            Return to Platform
          </button>
        </div>
      </div>

      {err ? (
        <div className="ft-card ft-card--error">
          <div className="ft-errorText">{err}</div>
        </div>
      ) : null}

      <div className="ft-card">
        <div className="ft-card__header">
          <div className="ft-card__headerTitle">What’s next</div>
          <div className="ft-card__headerSub">You’re all set.</div>
        </div>
        <div className="ft-card__body">
          <ul className="ft-listPlain">
            {lines.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>

          {sessionId ? (
            <div className="ft-hint" style={{ marginTop: 10 }}>
              Checkout reference: <b>{sessionId}</b>
            </div>
          ) : null}

          <div className="ft-hint" style={{ marginTop: 10 }}>
            {loading
              ? "Syncing billing details…"
              : summary
              ? "Billing details synced."
              : "Billing details will appear once Stripe webhooks sync."}
          </div>
        </div>
      </div>
    </div>
  );
}